import palette from '/b2b/common/theme/palette';
import startCase from 'lodash/startCase';
import { translate } from '/b2b/common/helpers/i18n';
import unescape from 'lodash/unescape';

// "New value" chip colors (osano-50 bg / osano-800 fg).
const CHANGE_NEW_VALUE_BG = '#F5F0FF';
const CHANGE_NEW_VALUE_FG = '#3E1A7F';

// Translation keys for known change paths, reusing the form editor's own field
// labels so the activity log matches what the user sees when editing.
const PATH_LABEL_KEYS = {
    'settings.orgIds': 'common.organizations',
    'settings.emailIngestionConfig.allowListEmails': 'dsar.emailSettings.allowListEmails.label',
    'settings.emailIngestionConfig.escalationEmails': 'dsar.emailSettings.escalationEmails.label',
    'styling.showHeader': 'dsar.formSettings.showFormName',
    'styling.stylesheetURL': 'common.fields.stylesheetUrl.label',
    'styling.instructions': 'common.instructions',
};

// Palette colours reuse the styling editor's colour labels. Some palette keys
// differ from their schema key (requiredColor → asterisk, formBackgroundColor →
// background), so they are mapped explicitly.
const PALETTE_LABEL_KEYS = {
    primaryColor: 'dsar.formStyling.schema.primaryColor',
    errorColor: 'dsar.formStyling.schema.errorColor',
    fieldTextColor: 'dsar.formStyling.schema.fieldTextColor',
    helperTextColor: 'dsar.formStyling.schema.helperTextColor',
    backgroundColor: 'dsar.formStyling.schema.backgroundColor',
    formBackgroundColor: 'dsar.formStyling.schema.backgroundColor',
    asteriskColor: 'dsar.formStyling.schema.asteriskColor',
    requiredColor: 'dsar.formStyling.schema.asteriskColor',
};

// A change's `label` is the entity's own name. For default (un-customized)
// templates that name is itself an i18n key (e.g.
// "dsar.formTemplates.rejection.appealed.name"), so resolve it. Free-text names
// (custom fields, renamed templates) fall through unchanged.
// Key-shaped: only word characters/hyphens and dots (a single char class, no
// nested quantifier, so linear-time), then 3+ non-empty dot-separated segments.
// Requiring 2+ dots keeps single-dot free text like "acme.com" or "v1.2" from
// being treated as a key — translate() records missing keys in dev/ci, so a
// looser guard would pollute that list.
const KEY_CHARS = /^[a-z0-9_.-]+$/i;
const isKeyShaped = label => {
    if (typeof label !== 'string' || !KEY_CHARS.test(label)) {
        return false;
    }

    const segments = label.split('.');
    return segments.length >= 3 && segments.every(Boolean);
};
const resolveLabel = label => (isKeyShaped(label) ? translate(label) : label);

// Human label for a change: a translated label for known setting/styling paths
// and palette colours, an item's own `label` for fields plus the changed
// property, or a startCased path segment as a fallback.
const labelForChange = ({ path = '', label: rawLabel } = {}) => {
    const label = resolveLabel(rawLabel);
    if (PATH_LABEL_KEYS[path]) {
        return translate(PATH_LABEL_KEYS[path]);
    }

    if (path.startsWith('styling.palette.')) {
        const colour = path.slice('styling.palette.'.length);
        return PALETTE_LABEL_KEYS[colour]
            ? translate(PALETTE_LABEL_KEYS[colour])
            : startCase(colour);
    }

    const segments = path.split('.');
    if (path.startsWith('fields.')) {
        // fields.{id} → the field; fields.{id}.{prop} → that property of it.
        const base = label || startCase(segments[1]);
        if (segments.length <= 2) {
            return base;
        }

        // "Display Options" has its own UI label; other props startCase the segment.
        const attr =
            segments[2] === 'displayOptions'
                ? translate('dsar.formFields.fields.displayOptions.title')
                : startCase(segments[2]);
        return `${base}: ${attr}`;
    }

    if (path.startsWith('requestTypes.')) {
        // requestTypes.{id}.{attr} → "{request type}: {attribute}".
        const base = label || startCase(segments[1]);
        return segments.length > 2 ? `${base}: ${startCase(segments[2])}` : base;
    }

    if (path.startsWith('templates.')) {
        // templates.{id}.{attr} → "{template}: {attribute}".
        const base = label || startCase(segments[1]);
        return segments.length > 2 ? `${base}: ${startCase(segments[2])}` : base;
    }

    return label || startCase(segments[segments.length - 1]);
};

// Stored form values are HTML-encoded (an apostrophe as &apos;, etc.). Decode
// them so they render as text — matching how the resource name renders — before
// re-escaping for the [html] block below. unescape runs first (it does not
// handle &apos;), then &apos; is normalized, so a double-encoded &amp;apos; still
// resolves to an apostrophe.
const decodeEntities = value => unescape(String(value)).replace(/&apos;/gi, "'");

// Values go into a BBCode [html] block, so escape HTML and BBCode brackets to
// keep a value like "</html]" or markup from closing the block early.
const escapeValue = value =>
    String(value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/\[/g, '&#91;')
        .replace(/]/g, '&#93;');

// Same, plus quote-escaping for use inside an HTML attribute (the title tooltip).
const escapeAttr = value => escapeValue(value).replace(/"/g, '&quot;');

const TRUNCATE_AT = 80;

// A change value as { display, title }: the decoded value truncated for display,
// keeping the full value in `title` (a hover tooltip) when truncated so long
// text stays inspectable.
const renderValue = value => {
    if (value === null || value === undefined || value === '') {
        return { display: '—', title: null };
    }

    // Collection values arrive as string arrays or, for orgs, { label, value }
    // pairs — show the label (name) for those.
    const asText = item =>
        item && typeof item === 'object' ? item.label ?? item.value ?? '' : item;
    const text = decodeEntities(
        Array.isArray(value) ? value.map(asText).join(', ') : String(value)
    );
    if (text.length > TRUNCATE_AT) {
        return {
            display: escapeValue(`${text.slice(0, TRUNCATE_AT)}…`),
            title: escapeAttr(text),
        };
    }

    return { display: escapeValue(text), title: null };
};

// UI labels for a field's Display Options state (the backend sends a stable token).
const DISPLAY_OPTION_LABEL_KEYS = {
    all: 'dsar.formFields.fields.displayOptions.enabledAll',
    specific: 'dsar.formFields.fields.displayOptions.hasRequestConditions',
    disabled: 'dsar.formFields.fields.displayOptions.disabled',
};

// A field's Display Options change value: { option, requestTypes }. Render the
// option in UI language (localized), appending the selected request-type names for
// "specific". Falls back to renderValue for anything unexpected.
const renderDisplayOption = value => {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
        return renderValue(value);
    }

    const { option, requestTypes = [] } = value;
    const optionLabel = DISPLAY_OPTION_LABEL_KEYS[option]
        ? translate(DISPLAY_OPTION_LABEL_KEYS[option])
        : startCase(String(option ?? ''));
    const names = requestTypes.map(name => decodeEntities(String(name)));
    const text =
        option === 'specific' && names.length ? `${optionLabel}: ${names.join(', ')}` : optionLabel;

    if (text.length > TRUNCATE_AT) {
        return {
            display: escapeValue(`${text.slice(0, TRUNCATE_AT)}…`),
            title: escapeAttr(text),
        };
    }

    return { display: escapeValue(text), title: null };
};

// Surfaces the source form's name for the %{sourceFormName} placeholder. A copied
// form's primary resource is the new form, so the source is carried in
// metadata.sourceFormName. Form names are stored HTML-encoded, so decode entities
// (as the change formatter does) to avoid rendering e.g. "Charlie&apos;s Form".
// Returns a non-empty '—' when absent so translateObject always replaces the
// placeholder.
export const sourceFormNameFormatter = (_value, auditEventData = {}) => {
    const name = auditEventData?.metadata?.sourceFormName;

    return name ? decodeEntities(name) : '—';
};

// Renders an audit event's metadata.changes[] (the standard change-set contract:
// an array of { action, path, before, after, changeType, label? } objects) as a
// list of items — struck-through "old" → "new" chips for a MODIFIED change, a
// single chip for ADDED/REMOVED. Reads the array from the event's metadata; also
// accepts an array passed directly (the first arg) for reuse/testing. Returns a
// BBCode [html] block, or a non-empty '—' when there are no changes so
// translateObject always replaces the %{changes} placeholder.
export const changeFormatter = (value, auditEventData = {}) => {
    const changes = Array.isArray(value) ? value : auditEventData?.metadata?.changes;
    // Return a non-empty string so translateObject always replaces the
    // %{changes} placeholder (a falsy value would leave it literal).
    if (!Array.isArray(changes) || changes.length === 0) {
        return '—';
    }

    const { grey } = palette.common;
    const oldChip = `background:${grey[100]};border-radius:4px;padding:1px 6px;text-decoration:line-through;text-decoration-color:${grey[400]};`;
    const newChip = `background:${CHANGE_NEW_VALUE_BG};color:${CHANGE_NEW_VALUE_FG};border-radius:4px;padding:1px 6px;`;
    const arrow = `color:${grey[500]};margin:0 10px;`;
    const listStyle = 'list-style:none;padding-left:0;margin:4px 0;';
    const itemStyle = 'margin-bottom:6px;';

    const chip = (style, { display, title } = {}) =>
        `<span style="${style}"${title ? ` title="${title}"` : ''}>${display}</span>`;

    const listItems = changes
        .map(change => {
            const { action, before, after } = change || {};
            const label = `<b>${escapeValue(decodeEntities(labelForChange(change)))}</b>`;
            // Display Options carry a { option, requestTypes } value rendered in UI
            // language; every other change is a plain scalar/collection value.
            const render = change?.path?.endsWith('.displayOptions')
                ? renderDisplayOption
                : renderValue;

            if (action === 'ADDED') {
                return `<li style="${itemStyle}">${label}&nbsp;&nbsp;${chip(newChip, render(after))}</li>`;
            }

            if (action === 'REMOVED') {
                return `<li style="${itemStyle}">${label}&nbsp;&nbsp;${chip(oldChip, render(before))}</li>`;
            }

            return `<li style="${itemStyle}">${label}&nbsp;&nbsp;${chip(
                oldChip,
                render(before)
            )}${chip(arrow, { display: '→' })}${chip(newChip, render(after))}</li>`;
        })
        .join('');
    return `[html]<ul style="${listStyle}">${listItems}</ul>[/html]`;
};
